import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import { Loader2, BarChart2, Users, CheckCircle, AlertCircle, TrendingUp, Download, MessageCircle, MessageSquare, UserCheck, Calendar, X, Ticket, TicketCheck, Clock, MessageSquareDot, ClipboardList, Search, ChevronLeft, ChevronRight, Utensils, RefreshCw, Eye, Flag } from 'lucide-react';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import companyAdminApi, { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { Link } from 'react-router-dom';

const TABS = [
  { key: 'ai_breakdown', label: 'AI Usage' },
  { key: 'ai_usage', label: 'AI Usage Report' },
  { key: 'ai_customers', label: 'Customers Using AI' },
  { key: 'chat_activity', label: 'Chat Activity' },
  { key: 'chat_stats', label: 'Chat Stats' },
  { key: 'tickets', label: 'Tickets Report' },
  { key: 'customer_reports', label: 'Customer Reports' },
  { key: 'diet_plans', label: 'Diet Plan Report' },
];

const PROMPT_LABELS = {
  chat: 'Chat',
  nutrition: 'Nutrition',
  image: 'Image',
  moderate_relax: 'Flexible Diet',
};

const PROMPT_COLORS = {
  chat: '#3b82f6',
  nutrition: '#22c55e',
  image: '#a855f7',
  moderate_relax: '#f59e0b',
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-xs text-muted-foreground font-medium">{label}</div>
        <div className="text-2xl font-bold text-foreground mt-0.5">{value ?? 0}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// ─── AI Usage Report Tab ────────────────────────────────────────────────────
function AIUsageReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminApi.get(`/admin/reports/ai-usage?days=${days}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pieData = (data?.by_prompt_type || []).map(p => ({
    name: PROMPT_LABELS[p.type] || p.type,
    value: p.calls,
    color: PROMPT_COLORS[p.type] || '#888ea8',
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading report...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">AI prompt usage statistics and trends</p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Period:</span>
          {[7, 14, 30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                days === d
                  ? 'bg-primary text-white border-primary'
                  : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BarChart2} label="Total AI Calls" value={data?.total_calls?.toLocaleString()} color="bg-blue-500" />
        <StatCard icon={CheckCircle} label="Successful" value={data?.total_success?.toLocaleString()} sub={`${data?.success_rate}% success rate`} color="bg-green-500" />
        <StatCard icon={AlertCircle} label="Failed" value={data?.total_failed?.toLocaleString()} color="bg-red-400" />
        <StatCard icon={TrendingUp} label="Avg Per Day" value={Math.round((data?.total_calls || 0) / (data?.period_days || 30)).toLocaleString()} color="bg-indigo-500" />
      </div>

      {/* Daily Trend Chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Daily Usage Trend</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data?.daily_trend || []} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#888ea8' }}
              tickFormatter={v => v.slice(5)}
              interval={Math.floor((data?.daily_trend?.length || 30) / 8)}
            />
            <YAxis tick={{ fontSize: 10, fill: '#888ea8' }} />
            <Tooltip
              formatter={(v, name) => [v, name === 'success' ? 'Success' : 'Failed']}
              labelFormatter={l => `Date: ${l}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="success" name="Success" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
            <Bar dataKey="failed" name="Failed" stackId="a" fill="#ef4444" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Prompt Type + Model Breakdown */}
      <div className="grid md:grid-cols-2 gap-5">
        {/* Prompt Type Pie */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">By Prompt Type</h3>
          {pieData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={65} innerRadius={35}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [v.toLocaleString(), 'Calls']} contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {(data?.by_prompt_type || []).map(p => (
                  <div key={p.type} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PROMPT_COLORS[p.type] || '#888' }} />
                      <span className="text-muted-foreground">{PROMPT_LABELS[p.type] || p.type}</span>
                    </div>
                    <div className="flex gap-2 text-right">
                      <span className="font-semibold text-foreground">{p.calls.toLocaleString()}</span>
                      <span className="text-muted-foreground">({p.calls > 0 ? Math.round((p.success / p.calls) * 100) : 0}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No data in this period</div>
          )}
        </div>

        {/* By Model Table */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">By AI Model</h3>
          {data?.by_model?.length > 0 ? (
            <div className="space-y-2.5">
              {data.by_model.slice(0, 8).map((m, i) => {
                const rate = m.calls > 0 ? Math.round((m.success / m.calls) * 100) : 0;
                return (
                  <div key={m.model_id} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-foreground truncate">{m.model_name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${rate}%` }} />
                        </div>
                        <span className="text-[10px] text-muted-foreground w-8 text-right">{rate}%</span>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-foreground shrink-0">{m.calls.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No data in this period</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AI Customers Report Tab ────────────────────────────────────────────────
function AICustomersReport() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [planStatus, setPlanStatus] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminApi.get('/admin/reports/ai-customers', {
        params: { page, limit: 20, search, plan_status: planStatus }
      });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, planStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const exportCSV = () => {
    const rows = [
      ['Name', 'Email', 'Phone', 'AI Messages', 'AI Sessions', 'Plan Status', 'Expiry Date', 'Price', 'Account Status'],
      ...customers.map(c => [
        c.name, c.email, c.phone_number || '', c.total_messages || 0, c.total_sessions || 0,
        c.plan_status, formatDate(c.ai_plan_expiry), c.ai_plan_price || '', c.status
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'ai_customers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{total}</span> customers with AI plan
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Search name or email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none focus:border-primary w-52"
          />
          <select
            style={{ width: 'auto', display: 'inline-block' }}
            className="form-select text-sm py-1.5 px-2 pr-7"
            value={planStatus}
            onChange={e => { setPlanStatus(e.target.value); setPage(1); }}
          >
            <option value="">All Plan Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Users className="w-10 h-10 mb-3 opacity-50" />
            <p className="text-sm">No AI plan customers found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Usage</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Expiry Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {customers.map(c => (
                <tr key={String(c.user_id)} className="hover:bg-muted">
                  <td className="px-4 py-3 font-medium text-foreground">{c.name || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone_number || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    {c.total_messages > 0 ? (
                      <div>
                        <div className="text-sm font-semibold text-foreground">{c.total_messages}</div>
                        <div className="text-[10px] text-muted-foreground">{c.total_sessions} session{c.total_sessions !== 1 ? 's' : ''}</div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">No usage</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.plan_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {c.plan_status === 'active' ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <AlertCircle className="w-3 h-3" />
                      )}
                      {c.plan_status === 'active' ? 'Active' : 'Expired'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {formatDate(c.ai_plan_expiry)}
                    {c.ai_plan_expiry && new Date(c.ai_plan_expiry) < new Date() && (
                      <div className="text-[10px] text-red-400">Expired</div>
                    )}
                    {c.ai_plan_expiry && new Date(c.ai_plan_expiry) >= new Date() && (() => {
                      const daysLeft = Math.ceil((new Date(c.ai_plan_expiry) - new Date()) / 86400000);
                      return daysLeft <= 7 ? (
                        <div className="text-[10px] text-amber-500">{daysLeft}d left</div>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.ai_plan_price ? `$${c.ai_plan_price}` : '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === 'active' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-muted-foreground'
                    }`}>
                      {c.status || 'active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/customers/${c.user_id}`}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40"
              >Prev</button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Customer food / intake issue reports (reported_issues) ─────────────────
function CustomerReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const limit = 20;

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const foodLabel = (row) => {
    if (row.food_name) return row.food_name;
    if (row.api_id) return `External (${row.api_id})`;
    if (row.food_bank) return `Bank: ${row.food_bank}`;
    return '—';
  };

  const issuePreview = (text, max = 72) => {
    if (!text) return '—';
    const t = String(text).trim();
    return t.length <= max ? t : `${t.slice(0, max)}…`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getReportedIssues({
        page,
        limit,
        search: appliedSearch || undefined,
      });
      setReports(res.data.reports || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load customer reports');
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Food and intake issues submitted by customers (newest first).{' '}
          <span className="font-semibold text-foreground">{total}</span> total
        </p>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            placeholder="Search issue text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none focus:border-primary min-w-[200px]"
          />
          <button
            type="submit"
            className="px-3 py-1.5 text-sm font-medium border border-border rounded-lg bg-card text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Flag className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No customer reports found.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Food</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Issue</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Reported</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {reports.map((row) => (
                <tr key={String(row._id)} className="hover:bg-muted">
                  <td className="px-4 py-3 font-medium text-foreground max-w-[200px]">{foodLabel(row)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{row.customer_name || '—'}</div>
                    {row.customer_email && (
                      <div className="text-xs text-muted-foreground truncate max-w-[220px]">{row.customer_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-md">{issuePreview(row.issue)}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {formatDateTime(row.createdAt || row.date)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => { setSelected(row); setDetailOpen(true); }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {detailOpen && selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDetailOpen(false)}
          role="presentation"
        >
          <div
            className="bg-card rounded-xl border border-border shadow-xl max-w-lg w-full app-modal-shell p-6 space-y-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-report-detail-title"
          >
            <div className="flex items-start justify-between gap-2">
              <h2 id="customer-report-detail-title" className="text-lg font-semibold text-foreground">
                Report detail
              </h2>
              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Customer-submitted food or intake issue</p>

            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Food</div>
              <p className="font-medium text-foreground mt-1">{foodLabel(selected)}</p>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer</div>
              <p className="font-medium text-foreground mt-1">{selected.customer_name || '—'}</p>
              {selected.customer_email && (
                <p className="text-sm text-muted-foreground">{selected.customer_email}</p>
              )}
            </div>
            {(selected.api_id || selected.food_bank) && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selected.api_id && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">API ID</div>
                    <p className="mt-1 break-all">{selected.api_id}</p>
                  </div>
                )}
                {selected.food_bank && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Food bank</div>
                    <p className="mt-1">{selected.food_bank}</p>
                  </div>
                )}
              </div>
            )}
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Reported issue</div>
              <p className="mt-2 whitespace-pre-wrap rounded-lg border border-border bg-muted p-3 text-sm text-foreground">
                {selected.issue || '—'}
              </p>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Submitted</div>
              <p className="text-sm text-foreground mt-1">{formatDateTime(selected.createdAt || selected.date)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Shared Period Selector with Custom Date Range ──────────────────────────
function PeriodSelector({ days, setDays, fromDate, setFromDate, toDate, setToDate, customMode, setCustomMode, onApply }) {
  const today = new Date().toISOString().split('T')[0];
  const [localFrom, setLocalFrom] = React.useState(fromDate || '');
  const [localTo, setLocalTo] = React.useState(toDate || today);

  const applyCustom = () => {
    if (!localFrom || !localTo) return;
    setFromDate(localFrom);
    setToDate(localTo);
    setCustomMode(true);
    onApply(null, localFrom, localTo);
  };

  const clearCustom = () => {
    setCustomMode(false);
    setLocalFrom('');
    setLocalTo(today);
    setFromDate('');
    setToDate('');
    onApply(30, '', '');
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Preset buttons */}
      {[7, 14, 30, 60, 90].map(d => (
        <button
          key={d}
          onClick={() => { setDays(d); setCustomMode(false); onApply(d, '', ''); }}
          className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
            !customMode && days === d
              ? 'bg-primary text-white border-primary'
              : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
          }`}
        >
          {d}d
        </button>
      ))}

      {/* Divider */}
      <span className="text-muted-foreground text-xs select-none px-1">|</span>

      {/* Custom date range */}
      <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 transition-colors ${customMode ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
        <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          type="date"
          value={localFrom}
          max={localTo || today}
          onChange={e => setLocalFrom(e.target.value)}
          className="text-xs text-foreground outline-none bg-transparent w-28 cursor-pointer"
          title="From date"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <input
          type="date"
          value={localTo}
          min={localFrom}
          max={today}
          onChange={e => setLocalTo(e.target.value)}
          className="text-xs text-foreground outline-none bg-transparent w-28 cursor-pointer"
          title="To date"
        />
        <button
          onClick={applyCustom}
          disabled={!localFrom || !localTo}
          className="ml-1 px-2 py-0.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          Apply
        </button>
        {customMode && (
          <button onClick={clearCustom} className="ml-0.5 text-muted-foreground hover:text-red-500 transition-colors" title="Clear custom range">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Chat Activity Report Tab ────────────────────────────────────────────────
function ChatActivityReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customMode, setCustomMode] = useState(false);

  const fetchData = useCallback(async (d = days, from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const params = (from && to)
        ? `from_date=${from}&to_date=${to}`
        : `days=${d || 30}`;
      const res = await companyAdminApi.get(`/admin/reports/chat-activity?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days, fromDate, toDate]);

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading chat activity...
      </div>
    );
  }

  const typeColors = {
    text: '#3b82f6',
    image: '#a855f7',
    audio: '#f59e0b',
    video: '#22c55e',
    file: '#06b6d4',
  };

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Customer chat engagement and message activity</p>
          {customMode && fromDate && toDate && (
            <p className="text-xs text-primary mt-0.5 font-medium">{fromDate} &rarr; {toDate}</p>
          )}
        </div>
        <PeriodSelector
          days={days} setDays={setDays}
          fromDate={fromDate} setFromDate={setFromDate}
          toDate={toDate} setToDate={setToDate}
          customMode={customMode} setCustomMode={setCustomMode}
          onApply={(d, from, to) => { if (d) setDays(d); fetchData(d, from, to); }}
        />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MessageCircle} label="Total Messages" value={(data?.total_messages || 0).toLocaleString()} color="bg-blue-500" />
        <StatCard icon={Users} label="Customer Messages" value={(data?.by_customer || 0).toLocaleString()} sub="Sent by customers" color="bg-green-500" />
        <StatCard icon={UserCheck} label="Agent Replies" value={(data?.by_agent || 0).toLocaleString()} sub="Sent by agents" color="bg-indigo-500" />
        <StatCard icon={TrendingUp} label="Avg Per Day" value={Math.round((data?.total_messages || 0) / (data?.period_days || days || 30)).toLocaleString()} color="bg-amber-500" />
      </div>

      {/* Daily Trend */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Daily Message Volume</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data?.daily_trend || []} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#888ea8' }}
              tickFormatter={v => v.slice(5)}
              interval={Math.floor((data?.daily_trend?.length || 30) / 8)}
            />
            <YAxis tick={{ fontSize: 10, fill: '#888ea8' }} allowDecimals={false} />
            <Tooltip
              formatter={(v) => [v, 'Messages']}
              labelFormatter={l => `Date: ${l}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="count" name="Messages" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Customers + Message Types */}
      <div className="grid md:grid-cols-2 gap-5">
        {/* Top Active Customers */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Top Active Customers</h3>
          {(data?.top_customers || []).length > 0 ? (
            <div className="space-y-2.5">
              {data.top_customers.map((c, i) => {
                const maxCount = data.top_customers[0]?.message_count || 1;
                const pct = Math.round((c.message_count / maxCount) * 100);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-0.5">
                        <span className="text-xs font-medium text-foreground truncate">{c.name}</span>
                        <span className="text-xs font-semibold text-foreground ml-2 shrink-0">{c.message_count}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No chat data in this period</div>
          )}
        </div>

        {/* Message Types */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Message Types</h3>
          {(data?.message_types || []).length > 0 ? (
            <div className="space-y-3">
              {data.message_types.map((t, i) => {
                const total = data.message_types.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((t.count / total) * 100) : 0;
                const color = typeColors[t.type] || PIE_COLORS[i % PIE_COLORS.length];
                return (
                  <div key={t.type} className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground capitalize">{t.type}</span>
                        <span className="font-semibold text-foreground">{t.count.toLocaleString()} <span className="text-muted-foreground font-normal">({pct}%)</span></span>
                      </div>
                      <div className="mt-0.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No data in this period</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Chat Stats Report Tab ──────────────────────────────────────────────────
function ChatStatsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customMode, setCustomMode] = useState(false);

  const fetchData = useCallback(async (d = days, from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const params = (from && to)
        ? `from_date=${from}&to_date=${to}`
        : `days=${d || 30}`;
      const res = await companyAdminApi.get(`/admin/reports/chat-stats?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days, fromDate, toDate]);

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading chat stats...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Agent performance and customer engagement metrics</p>
          {customMode && fromDate && toDate && (
            <p className="text-xs text-primary mt-0.5 font-medium">{fromDate} &rarr; {toDate}</p>
          )}
        </div>
        <PeriodSelector
          days={days} setDays={setDays}
          fromDate={fromDate} setFromDate={setFromDate}
          toDate={toDate} setToDate={setToDate}
          customMode={customMode} setCustomMode={setCustomMode}
          onApply={(d, from, to) => { if (d) setDays(d); fetchData(d, from, to); }}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Agent Performance Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Agent Reply Stats</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Messages sent by admins/agents in the period</p>
          </div>
          {(data?.agent_stats || []).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Agent</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Replies</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.agent_stats.map((a, i) => (
                  <tr key={i} className="hover:bg-muted">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground text-sm">{a.name}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-blue-600">{a.total_replies.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-semibold text-foreground">{a.unique_customers}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No agent activity in this period</div>
          )}
        </div>

        {/* Customer Engagement Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Customer Chat Engagement</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Top 20 most active customers by messages</p>
          </div>
          {(data?.customer_stats || []).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Messages</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Active Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.customer_stats.map((c, i) => (
                  <tr key={i} className="hover:bg-muted">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-green-600">{c.total_messages.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 font-medium">
                        {c.active_days}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No customer chat data in this period</div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Tickets Report Tab ──────────────────────────────────────────────────────
function TicketsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (d = days, from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const params = (from && to)
        ? `from_date=${from}&to_date=${to}`
        : `days=${d || 30}`;
      const res = await companyAdminApi.get(`/admin/reports/tickets?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days, fromDate, toDate]);

  useEffect(() => { fetchData(); }, []);

  const priorityColors = {
    high:   { bg: 'bg-red-100',    text: 'text-red-700',    bar: '#ef4444' },
    medium: { bg: 'bg-amber-100',  text: 'text-amber-700',  bar: '#f59e0b' },
    low:    { bg: 'bg-green-100',  text: 'text-green-700',  bar: '#22c55e' },
  };

  const filtered = (data?.per_customer || []).filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.customer_name || '').toLowerCase().includes(q) ||
           (c.customer_email || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tickets report...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Support ticket activity and customer engagement</p>
          {customMode && fromDate && toDate && (
            <p className="text-xs text-primary mt-0.5 font-medium">{fromDate} &rarr; {toDate}</p>
          )}
        </div>
        <PeriodSelector
          days={days} setDays={setDays}
          fromDate={fromDate} setFromDate={setFromDate}
          toDate={toDate} setToDate={setToDate}
          customMode={customMode} setCustomMode={setCustomMode}
          onApply={(d, from, to) => { if (d) setDays(d); fetchData(d, from, to); }}
        />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MessageSquareDot} label="Total Tickets" value={(data?.total_tickets || 0).toLocaleString()} color="bg-blue-500" />
        <StatCard icon={Clock} label="Open Tickets" value={(data?.open_count || 0).toLocaleString()} sub="Awaiting resolution" color="bg-amber-500" />
        <StatCard icon={TicketCheck} label="Closed Tickets" value={(data?.closed_count || 0).toLocaleString()} sub="Resolved" color="bg-green-500" />
        <StatCard icon={MessageCircle} label="Total Replies" value={(data?.total_replies || 0).toLocaleString()} sub="Notes added by agents" color="bg-indigo-500" />
      </div>

      {/* Daily Trend + Priority Breakdown */}
      <div className="grid md:grid-cols-3 gap-5">
        {/* Daily Trend Chart */}
        <div className="md:col-span-2 bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Tickets Created Per Day</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data?.daily_trend || []} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#888ea8' }}
                tickFormatter={v => v.slice(5)}
                interval={Math.floor((data?.daily_trend?.length || 30) / 8)}
              />
              <YAxis tick={{ fontSize: 10, fill: '#888ea8' }} allowDecimals={false} />
              <Tooltip
                formatter={(v, name) => [v, name === 'open' ? 'Open' : name === 'closed' ? 'Closed' : 'Total']}
                labelFormatter={l => `Date: ${l}`}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="open" name="Open" stackId="a" fill="#f59e0b" radius={[0,0,0,0]} />
              <Bar dataKey="closed" name="Closed" stackId="a" fill="#22c55e" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Priority Breakdown */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">By Priority</h3>
          {(data?.by_priority || []).length > 0 ? (
            <div className="space-y-4 mt-2">
              {['high', 'medium', 'low'].map(p => {
                const found = (data?.by_priority || []).find(r => r.priority === p);
                const count = found?.count || 0;
                const total = data?.total_tickets || 1;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const colors = priorityColors[p] || priorityColors.medium;
                return (
                  <div key={p}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} capitalize`}>
                        {p}
                      </span>
                      <span className="text-sm font-bold text-foreground">{count} <span className="text-xs text-muted-foreground font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: colors.bar }} />
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 border-t border-border mt-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Resolution Rate</span>
                  <span className="font-semibold text-green-600">
                    {data?.total_tickets > 0
                      ? Math.round(((data?.closed_count || 0) / data.total_tickets) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${data?.total_tickets > 0 ? Math.round(((data?.closed_count || 0) / data.total_tickets) * 100) : 0}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No tickets in this period</div>
          )}
        </div>
      </div>

      {/* Agent Replies Table */}
      {(data?.agent_replies || []).length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Agent Reply Stats</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Which user replied to how many tickets</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Agent / User</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tickets Replied To</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Replies</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Avg Replies / Ticket</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.agent_replies.map((a, i) => {
                const maxReplies = data.agent_replies[0]?.total_replies || 1;
                const pct = Math.round((a.total_replies / maxReplies) * 100);
                const avg = a.unique_tickets > 0 ? (a.total_replies / a.unique_tickets).toFixed(1) : '0';
                return (
                  <tr key={a.agent} className="hover:bg-muted">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{(a.agent || '?')[0].toUpperCase()}</span>
                        </div>
                        <span className="text-sm font-medium text-foreground">{a.agent}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-blue-600">{a.unique_tickets}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-indigo-600">{a.total_replies}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs font-semibold text-foreground">{avg}</span>
                    </td>
                    <td className="px-4 py-3 w-32">
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-Customer Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Per-Customer Breakdown</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Tickets created and replies received per customer</p>
          </div>
          <input
            type="text"
            placeholder="Search customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none focus:border-primary w-48"
          />
        </div>

        {filtered.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Tickets</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Open</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Closed</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Replies Received</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c, i) => (
                <tr key={c.customer_ref || i} className="hover:bg-muted">
                  <td className="px-4 py-3 text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground text-sm">{c.customer_name || 'Unknown'}</div>
                    <div className="text-xs text-muted-foreground">{c.customer_email || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-bold text-blue-600">{c.total_tickets}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.open_tickets > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        {c.open_tickets}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-semibold text-green-600">{c.closed_tickets}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-semibold text-indigo-600">{c.total_replies}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.open_tickets > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        <CheckCircle className="w-3 h-3" /> Resolved
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <MessageSquareDot className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">{search ? 'No customers match your search' : 'No ticket data in this period'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Reports Page ───────────────────────────────────────────────────────
// ─── Diet Plan Report Tab ────────────────────────────────────────────────────
function AIBreakdownReport() {
  const [summary, setSummary]     = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const limit = 20;

  // Filter inputs (not applied until Search is clicked)
  const [customerInput, setCustomerInput] = useState('');
  const [fromDateInput, setFromDateInput] = useState('');
  const [toDateInput, setToDateInput]     = useState('');

  // Applied filters
  const [filters, setFilters] = useState({ search: '', from_date: '', to_date: '' });

  const hasFilters = filters.search || filters.from_date || filters.to_date;

  const fetchData = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const params = { page: p, limit };
      if (f.search)     params.search     = f.search;
      if (f.from_date)  params.from_date  = f.from_date;
      if (f.to_date)    params.to_date    = f.to_date;
      const res = await companyAdminApi.get('/admin/reports/ai-breakdown', { params });
      setSummary(res.data.summary || {});
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('ai-breakdown error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(filters, page); }, [filters, page, fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters({ search: customerInput.trim(), from_date: fromDateInput, to_date: toDateInput });
  };

  const handleClear = () => {
    setCustomerInput(''); setFromDateInput(''); setToDateInput('');
    setPage(1);
    setFilters({ search: '', from_date: '', to_date: '' });
  };

  const totalPages = Math.ceil(total / limit);

  const statCards = [
    { label: 'Nutrition Count',       value: summary?.nutrition_count,       icon: Utensils,       color: 'bg-emerald-500' },
    { label: 'Image Count',           value: summary?.image_count,           icon: BarChart2,      color: 'bg-purple-500' },
    { label: 'Moderate-relax Count',  value: summary?.moderate_relax_count,  icon: TrendingUp,     color: 'bg-amber-500' },
    { label: 'AI Chat Count',         value: summary?.ai_chat_count,         icon: MessageCircle,  color: 'bg-blue-500' },
  ];

  return (
    <div className="space-y-6">
      {/* ── Summary Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${c.color}`}>
              <c.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground font-medium">{c.label}</div>
              <div className="text-2xl font-bold text-foreground mt-0.5">
                {loading && c.value === undefined ? <Loader2 className="w-5 h-5 animate-spin inline" /> : (c.value ?? 0)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Customer Table ── */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* ── Filter Bar ── */}
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end px-5 py-4 border-b border-border">
          {/* Customer Name */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Customer Name</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={customerInput}
                onChange={e => setCustomerInput(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="ai-breakdown-search"
              />
            </div>
          </div>

          {/* From Date */}
          <div className="min-w-[140px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">From Date</label>
            <input
              type="date"
              value={fromDateInput}
              onChange={e => setFromDateInput(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="ai-breakdown-from-date"
            />
          </div>

          {/* To Date */}
          <div className="min-w-[140px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">To Date</label>
            <input
              type="date"
              value={toDateInput}
              onChange={e => setToDateInput(e.target.value)}
              min={fromDateInput || undefined}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="ai-breakdown-to-date"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 items-end pb-0.5">
            <button type="submit"
              className="px-5 py-2 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary/90 transition-colors whitespace-nowrap"
              data-testid="ai-breakdown-search-btn">
              Search
            </button>
            {hasFilters && (
              <button type="button" onClick={handleClear}
                className="px-3 py-2 text-sm text-muted-foreground border border-border rounded-md flex items-center gap-1 hover:bg-muted whitespace-nowrap"
                data-testid="ai-breakdown-clear-btn">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </form>

        {/* Table title */}
        <div className="px-5 py-3 border-b border-border bg-muted">
          <h3 className="text-sm font-semibold text-foreground">Customer AI Chat Usage</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Customers ranked by AI chat messages sent{hasFilters ? '' : ' (all time)'}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
            <MessageCircle className="w-10 h-10 opacity-30" />
            <p className="text-sm">{filters.search ? 'No customers match your search.' : 'No AI chat activity found.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Chat Sessions</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Chat Messages</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nutrition Scans</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Image Scans</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {customers.map((row, i) => (
                  <tr key={i} className="hover:bg-muted transition-colors" data-testid={`ai-row-${i}`}>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{(page - 1) * limit + i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold shrink-0">
                          {(row.name || '?')[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground text-sm">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{row.email || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {row.sessions > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">{row.sessions}</span>
                      ) : <span className="text-[#ccc] text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.messages > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold">{row.messages}</span>
                      ) : <span className="text-[#ccc] text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.nutrition > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">{row.nutrition}</span>
                      ) : <span className="text-[#ccc] text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.image > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold">{row.image}</span>
                      ) : <span className="text-[#ccc] text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-40" data-testid="ai-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-40" data-testid="ai-next-btn">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        * Nutrition Scans = food nutrition AI lookups. Image Scans = food photo recognition scans. Moderate-relax totals are shown in summary cards above.
      </p>
    </div>
  );
}

function DietPlanReport() {
  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [page, setPage]               = useState(1);
  const limit = 20;

  // Filter state
  const [customerInput, setCustomerInput]   = useState('');
  const [creatorInput, setCreatorInput]     = useState('');
  const [fromDateInput, setFromDateInput]   = useState('');
  const [toDateInput, setToDateInput]       = useState('');

  // Applied filters (after Search is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '', creator_search: '', from_date: '', to_date: ''
  });

  const fetchData = useCallback(async (filters, p) => {
    setLoading(true);
    try {
      const params = { page: p, limit };
      if (filters.search)         params.search         = filters.search;
      if (filters.creator_search) params.creator_search = filters.creator_search;
      if (filters.from_date)      params.from_date      = filters.from_date;
      if (filters.to_date)        params.to_date        = filters.to_date;
      const res = await companyAdminApi.get('/admin/reports/diet-plans', { params });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('diet plan report error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(appliedFilters, page); }, [appliedFilters, page, fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters({
      search:         customerInput.trim(),
      creator_search: creatorInput.trim(),
      from_date:      fromDateInput,
      to_date:        toDateInput,
    });
  };

  const hasFilters = appliedFilters.search || appliedFilters.creator_search || appliedFilters.from_date || appliedFilters.to_date;

  const handleClear = () => {
    setCustomerInput('');
    setCreatorInput('');
    setFromDateInput('');
    setToDateInput('');
    setPage(1);
    setAppliedFilters({ search: '', creator_search: '', from_date: '', to_date: '' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <form onSubmit={handleSearch} className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Customer Name */}
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Customer Name</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={customerInput}
                onChange={e => setCustomerInput(e.target.value)}
                placeholder="Search customer..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="diet-plan-customer-search"
              />
            </div>
          </div>

          {/* Creator / User Name */}
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Created By (User Name)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={creatorInput}
                onChange={e => setCreatorInput(e.target.value)}
                placeholder="Search creator..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="diet-plan-creator-search"
              />
            </div>
          </div>

          {/* From Date */}
          <div className="min-w-[140px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">From Date</label>
            <input
              type="date"
              value={fromDateInput}
              onChange={e => setFromDateInput(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="diet-plan-from-date"
            />
          </div>

          {/* To Date */}
          <div className="min-w-[140px]">
            <label className="block text-xs font-medium text-muted-foreground mb-1">To Date</label>
            <input
              type="date"
              value={toDateInput}
              onChange={e => setToDateInput(e.target.value)}
              min={fromDateInput || undefined}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="diet-plan-to-date"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 items-end pb-0.5">
            <button
              type="submit"
              className="px-5 py-2 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary/90 transition-colors whitespace-nowrap"
              data-testid="diet-plan-search-btn"
            >
              Search
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md flex items-center gap-1 whitespace-nowrap"
                data-testid="diet-plan-clear-btn"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Summary */}
      {!loading && (
        <p className="text-xs text-muted-foreground">
          {total > 0
            ? `Showing ${Math.min((page - 1) * limit + 1, total)}–${Math.min(page * limit, total)} of ${total} record${total !== 1 ? 's' : ''} • sorted by most recent first`
            : 'No records found'}
        </p>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
            <Utensils className="w-10 h-10 opacity-30" />
            <p className="text-sm">{hasFilters ? 'No results match your filters.' : 'No diet plan records found.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Diet Plan Created By</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Strict Plan</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Moderate Plan</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Entry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((row, i) => (
                  <tr key={i} className="hover:bg-muted transition-colors" data-testid={`diet-plan-row-${i}`}>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{(page - 1) * limit + i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold shrink-0">
                          {(row.customer_name || '?')[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground">{row.customer_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{row.customer_email || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-semibold shrink-0">
                          {(row.creator_name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-xs">{row.creator_name || '—'}</p>
                          {row.creator_email && <p className="text-[10px] text-muted-foreground">{row.creator_email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.strict_count > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">{row.strict_count}</span>
                      ) : (
                        <span className="text-[#ccc] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.moderate_count > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold">{row.moderate_count}</span>
                      ) : (
                        <span className="text-[#ccc] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">{row.total_count}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {row.last_entry ? new Date(row.last_entry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="diet-plan-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pg = page <= 3 ? i + 1 : page + i - 2;
              if (pg < 1 || pg > totalPages) return null;
              return (
                <button
                  key={pg}
                  onClick={() => setPage(pg)}
                  className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${pg === page ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
                >
                  {pg}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="diet-plan-next-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ReportsPage() {
  const [activeTab, setActiveTab]   = useState('ai_breakdown');
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey(k => k + 1);
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setRefreshKey(k => k + 1);   // reset/reload when switching tabs
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Reports</h1>
            <p className="text-muted-foreground mt-1">AI usage analytics, customer insights, and customer-submitted food issues</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="reports-refresh-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-lg bg-card text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex gap-0">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-[#888ea8]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content — key={refreshKey} remounts component → triggers useEffect re-fetch */}
        {activeTab === 'ai_breakdown'  && <AIBreakdownReport  key={refreshKey} />}
        {activeTab === 'ai_usage'      && <AIUsageReport      key={refreshKey} />}
        {activeTab === 'ai_customers'  && <AICustomersReport  key={refreshKey} />}
        {activeTab === 'chat_activity' && <ChatActivityReport key={refreshKey} />}
        {activeTab === 'chat_stats'    && <ChatStatsReport    key={refreshKey} />}
        {activeTab === 'tickets'       && <TicketsReport      key={refreshKey} />}
        {activeTab === 'customer_reports' && <CustomerReportsTab key={refreshKey} />}
        {activeTab === 'diet_plans'    && <DietPlanReport     key={refreshKey} />}
      </div>
    </CompanyAdminLayout>
  );
}
